import React from 'react';

interface QuickStat {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  pulse?: boolean;
}

interface QuickStatsBarProps {
  stats?: {
    todayBookings: number;
    vehiclesOnRoad: number;
    pendingPayments: number;
    maintenanceAlerts: number;
    newCustomers: number;
  };
}

const QuickStatsBar: React.FC<QuickStatsBarProps> = ({ stats }) => {
  const quickStats: QuickStat[] = [
    {
      label: "Today's Bookings",
      value: stats?.todayBookings ?? 0,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      color: 'text-blue-600 bg-blue-50',
      pulse: (stats?.todayBookings ?? 0) > 0
    },
    {
      label: 'On Road Now',
      value: stats?.vehiclesOnRoad ?? 0,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      color: 'text-green-600 bg-green-50',
      pulse: (stats?.vehiclesOnRoad ?? 0) > 0
    },
    {
      label: 'Pending Payments',
      value: stats?.pendingPayments ?? 0,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'text-orange-600 bg-orange-50',
      pulse: (stats?.pendingPayments ?? 0) > 0
    },
    {
      label: 'Maintenance Alerts',
      value: stats?.maintenanceAlerts ?? 0,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      color: 'text-red-600 bg-red-50',
      pulse: (stats?.maintenanceAlerts ?? 0) > 0
    },
    {
      label: 'New Customers',
      value: stats?.newCustomers ?? 0,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
        </svg>
      ),
      color: 'text-purple-600 bg-purple-50',
      pulse: (stats?.newCustomers ?? 0) > 0
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        {quickStats.map((stat, index) => (
          <div
            key={index}
            className="flex items-center gap-3 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
          >
            <div className={`${stat.color} p-2 rounded-lg relative`}>
              {stat.pulse && (
                <span className="absolute top-0 right-0 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-current opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-current"></span>
                </span>
              )}
              {stat.icon}
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">{stat.label}</p>
              <p className="text-xl font-bold text-gray-900 group-hover:scale-110 transition-transform">
                {stat.value}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QuickStatsBar;
